import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { PageNav } from '@/components/ui/PageNav';
import { MobileSectionNav } from '@/components/ui/MobileSectionNav';
import { Disclosure } from '@/components/ui/Disclosure';
import { useSectionSpy } from '@/hooks/useSectionSpy';
import {
  FigureStressWindows,
  FigureCoverageGeometry,
  EvidencePlate,
  MemoEyebrow,
  SectionNum,
  CtaPrimary,
  CtaGhost,
  TrustChip,
} from '@/components/Patterns';
import { AnalyticalField } from '@/components/AnalyticalField';
import { Shield, Database, Users, FileText } from 'lucide-react';

/* ── Content (COPY-LOCKED — do not alter any visible sentence) ── */

const REALITY_PATTERNS = [
  {
    number: '01',
    title: 'SL Missed Despite Enough Headcount',
    description: "Coverage is good according to the plan and Finance concurs. Service level, however, falls short. When the math and the results don't match, the issue is typically architectural rather than numerical.",
  },
  {
    number: '02',
    title: 'Occupancy & Service Level Paradox',
    description: "Pressure rises when occupancy increases. Service level doesn't get better. This is not bad luck but structural brittleness.",
  },
  {
    number: '03',
    title: 'Forecast Accuracy Masking Interval Volatility',
    description: 'Your weekly prediction seems reasonable. Then frontline reality is broken by interval volatility, tail compression and burst clustering. Intervals show what aggregates conceal.',
  },
  {
    number: '04',
    title: "Adherence metrics that don't forecast results",
    description: 'The level of adherence is within target. SLA and ASA however, continue to stray. Structural tension arises when adherence and outcome do not correlate.',
  },
  {
    number: '05',
    title: 'Repeating stress intervals caused by friction and drift',
    description: "Every week the same intervals collapse. Different agents but the same pattern. That isn't randomness; it's system reinforcement.",
  },
];

const DOMAINS = [
  {
    title: 'Demand and Forecast Structure',
    items: ['Residual bias', 'Burst clustering', 'Tail volatility exposure', 'Density compression'],
  },
  {
    title: 'Coverage Architecture',
    items: ['Misdistribution across skills and queues', 'Shift boundary fragility', 'Coverage geometry mismatch'],
  },
  {
    title: 'Behavioral Amplification Within Intraday',
    items: ['Recurring stress windows', 'Elasticity breakdown', 'Coordination friction between planning and floor'],
  },
];

const DELIVERABLES = [
  {
    title: 'Executive Memorandum (PDF)',
    description: 'A communication-ready description of what is structurally true about your operation and why it performs the way it does.',
  },
  {
    title: 'Evidence Annex',
    description: 'Artifacts designed for internal review:',
    items: [
      'Interval level instability maps',
      'Residual decomposition charts',
      'Stress window overlays',
      'Coverage misalignment heatmaps',
      'Structural risk indicators',
    ],
  },
  {
    title: 'Ranked Recommendations',
    description: 'Operationally feasible actions prioritized according to structural effects.',
  },
];

const ENGAGEMENTS = [
  {
    number: '01',
    title: 'Pre Diagnostics Session',
    subtitle: null,
    body: 'A focused operational intake that determines whether instability is structural or in execution, and whether your project and data can support an OnPoint Diagnostics.',
    includes: [
      'Mapping of planning structure, queues, channels, SLAs',
      'Distinguishing between where instability is felt and where it is measured',
      'Confirming export sufficiency',
      'Deciding whether full diagnostics will materially help',
    ],
    meta: [
      ['Format', 'Remote Session: 60 to 90 minutes'],
      ['Outcome', 'Clear decision and scoped export checklist.'],
      ['Fee', 'Fixed fee of $250 per session.'],
    ],
  },
  {
    number: '02',
    title: 'OnPoint Diagnostics',
    subtitle: 'Enhance core analytic engagement using interval level WFM exports',
    body: null,
    includes: [
      'Demand shape and coverage geometry mapping',
      'Quantification of forecast distortions',
      'Identification of stress intervals',
      'Exposure of coordination friction',
    ],
    outputs: [
      'Structural Instability Snapshot',
      'Executive brief',
      'Evidence artifacts',
    ],
  },
  {
    number: '03',
    title: 'Ongoing Engagement',
    subtitle: 'Optional structural oversight after snapshot completion',
    body: null,
    includes: [
      'Structural refresh on a monthly and quarterly basis',
      'Embedded Workforce Management Partnership for interpretation and decision support',
      'Scenario modeling and stress testing',
    ],
  },
];

const GOOD_FIT = [
  'Persistent instability even though the team was appropriately staffed',
  '6 to 10 weeks of interval exports available',
  'Need clarity before major workforce decisions',
  'Value defensible analysis',
  'Must illustrate by example to the leadership',
];

const NOT_FIT = [
  'You want more dashboards for dashboards\u2019 sake',
  "You don't know how to export WFM Data",
  'You only want justification for additional hiring',
  'You are looking for a quick tactical fix to a transient problem',
];

const ATLAS_PATTERNS = [
  'Volatility bursts masked by aggregate accuracy',
  'Coverage mismatch fractures',
  'Behavioral drift amplification',
  'Occupancy and outcome paradoxes',
  'Reinforced stress cycles',
];

const INPUT_REQUIREMENTS = [
  'Arrivals logs',
  'Schedule and coverage reports',
  'SLA and ASA history',
  'Adherence and AHT variance',
  'Forecast files by interval',
];

/* ── Home post-hero rail — concise nav labels (owner-authorized, derived from
   existing headings) + target section ids ── */

const HOME_RAIL = [
  { id: 'home-reality', num: '01', label: 'Everyday Patterns' },
  { id: 'home-do', num: '02', label: 'What We Do' },
  { id: 'home-deliverables', num: '03', label: 'Deliverables' },
  { id: 'home-engagements', num: '04', label: 'Engagement Types' },
  { id: 'home-inputs', num: '05', label: 'Input Requirements' },
  { id: 'home-fit', num: '06', label: 'Who This Is For' },
  { id: 'home-atlas', num: '07', label: 'Instability Atlas' },
  { id: 'home-cta', num: '08', label: 'Begin Intake' },
];

const HOME_IDS = HOME_RAIL.map((s) => s.id);

export function Home() {
  const activeId = useSectionSpy(HOME_IDS, '0px 0px -80% 0px');
  // Everyday Patterns — single-open disclosure, first pattern open by default.
  const [openPattern, setOpenPattern] = useState(0);

  return (
    <div style={{ backgroundColor: 'var(--memo-field)' }}>
      <SEOHead
        title="OnPoint.Works | WFM Analytics Lab"
        description="When your workforce signals do not add up, we isolate why. We confirm whether your operational gap is structural, a forecast artifact, coverage misalignment, or behavioral distortion."
      />

      {/* ── Hero — asymmetric editorial + ambient isopleth field (APPROVED — retained) ── */}
      <section className="relative border-b border-memo-line py-24 md:py-32">
        <div className="absolute inset-0 grid-texture pointer-events-none" aria-hidden="true" />
        <AnalyticalField variant="hero" />
        <div className="container-site relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_1fr] lg:gap-14 items-start">
            <div>
              <h1
                data-testid="hero-heading"
                className="font-display text-5xl md:text-6xl font-medium tracking-tight leading-[1.06]"
                style={{ color: 'var(--memo-ink)' }}
              >
                Instability Classification for
                <br />
                <span className="italic" style={{ color: 'var(--memo-muted)' }}>CX Operations.</span>
              </h1>
              <h2 className="mt-6 text-xl md:text-2xl font-display font-normal leading-snug" style={{ color: 'var(--memo-ink)' }}>
                Operational instability is structural if not tactical.
              </h2>
              <p
                data-testid="hero-description"
                className="mt-5 text-[1.05rem] max-w-[60ch] leading-relaxed"
                style={{ color: 'var(--memo-ink-2)' }}
              >
                Before adding more headcount, we identify the source of your operational instability without requiring system access, and determine whether your gap is structural.
              </p>
              <div className="flex flex-wrap gap-3 mt-9">
                <CtaPrimary to="/intake" testid="hero-intake-cta">Begin Intake</CtaPrimary>
                <CtaGhost to="/services">View Services Overview</CtaGhost>
              </div>
              <div className="mt-12 flex flex-wrap gap-3">
                <TrustChip icon={Shield} label="No System Access" />
                <TrustChip icon={Database} label="Offline Data Processing" />
                <TrustChip icon={Users} label="No Third Party Routing" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Post-hero — persistent left nav (lg) + mobile section nav (<lg) + canvas ── */}
      <div className="container-site spine-shell lg:grid lg:grid-cols-[16rem_minmax(0,1fr)]">
        <PageNav items={HOME_RAIL} activeId={activeId} label="Home sections" />

        <div className="lg:min-w-0 lg:border-l lg:border-memo-line lg:pl-10">
          <MobileSectionNav items={HOME_RAIL} activeId={activeId} label="Home sections" />

      {/* ── Everyday Patterns — interactive editorial disclosure ── */}
      <section id="home-reality" className="py-16 md:py-20 border-b border-memo-line">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] lg:gap-14 items-start">
          <div className="lg:sticky lg:top-[6rem] lg:self-start">
            <MemoEyebrow>Reality Check</MemoEyebrow>
            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 mb-4">Everyday Patterns We See</h2>
            <p className="text-lg leading-relaxed max-w-[48ch]" style={{ color: 'var(--memo-ink-2)' }}>
              You have seen at least one of these if you operate a contact center at scale.
            </p>
          </div>

          <div className="border-t border-memo-line">
            {REALITY_PATTERNS.map((p, i) => (
              <Disclosure
                key={p.number}
                id={`pattern-${p.number}`}
                isOpen={openPattern === i}
                onToggle={() => setOpenPattern(openPattern === i ? -1 : i)}
                trigger={
                  <span className={`flex items-baseline gap-5 ${openPattern === i ? 'text-memo-ink' : 'text-memo-ink-2'}`}>
                    <SectionNum>{p.number}</SectionNum>
                    <span className="font-display text-xl md:text-[1.35rem] font-medium leading-snug">{p.title}</span>
                  </span>
                }
              >
                <p className="pl-[3.4rem] text-[0.98rem] leading-relaxed max-w-[58ch]" style={{ color: 'var(--memo-ink-2)' }}>
                  {p.description}
                </p>
              </Disclosure>
            ))}
          </div>
        </div>

        <p className="mt-8 text-sm border-t border-memo-line pt-6 max-w-[62ch]" style={{ color: 'var(--memo-muted)' }}>
          These are typical business-related operational events. We examine and deal with them as symptoms of underlying structural dynamics rather than anomalies.
        </p>
      </section>

      {/* ── What We Actually Do — narrow lead + 3 analytical domains + wide evidence ── */}
      <section id="home-do" className="py-16 md:py-20 bg-memo-paper border-b border-memo-line">
        <div className="max-w-[70ch] mb-12">
          <MemoEyebrow>What We Actually Do</MemoEyebrow>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 mb-4">We don&rsquo;t build dashboards for dashboards’ sake.</h2>
          <p className="text-lg max-w-[62ch] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>
            We pinpoint the problem areas in your planning and intraday system without needing any extra data. The data you already export is enough.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {DOMAINS.map((d) => (
            <div key={d.title} className="border-t border-memo-line-2 pt-5">
              <h3 className="font-display text-lg font-medium mb-4">{d.title}</h3>
              <ul className="space-y-2">
                {d.items.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-memo-signal">
                    <span aria-hidden="true">—</span>
                    <span className="text-memo-ink-2" style={{ color: 'var(--memo-ink-2)' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8">
          <EvidencePlate tag="Evidence · stress windows">
            <FigureStressWindows />
          </EvidencePlate>
          <EvidencePlate tag="Evidence · coverage geometry">
            <FigureCoverageGeometry />
          </EvidencePlate>
        </div>

        <div className="mt-10 p-5 border border-memo-line bg-memo-paper rounded">
          <p className="text-sm leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>
            All analysis is applied from exported files offline. No credentials. No live integration. No third party routing.
          </p>
        </div>
      </section>

      {/* ── Deliverables — hairline ledger ── */}
      <section id="home-deliverables" className="py-16 md:py-20 border-b border-memo-line">
        <div className="mb-10">
          <MemoEyebrow>Deliverables</MemoEyebrow>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 mb-3">Insights You Can Really Use</h2>
        </div>

        <div className="flex flex-col gap-0">
          {DELIVERABLES.map((d, idx) => (
            <div key={d.title} className="grid grid-cols-[3rem_1fr] lg:grid-cols-[4rem_1fr] gap-6 py-10 border-b border-memo-line">
              <SectionNum>{String(idx + 1).padStart(2, '0')}</SectionNum>
              <div>
                <h3 className="font-display text-2xl font-medium">{d.title}</h3>
                <p className="mt-3 text-[0.95rem] leading-relaxed max-w-[62ch]" style={{ color: 'var(--memo-ink-2)' }}>{d.description}</p>
                {d.items && (
                  <ul className="mt-3 space-y-1.5">
                    {d.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-memo-signal">
                        <span aria-hidden="true">—</span>
                        <span className="text-sm" style={{ color: 'var(--memo-muted)' }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-9 flex flex-col gap-0">
          {[
            'Usual lead time is three weeks from the Pre Diagnostics Session',
            'By default, data is kept for 14 days, which can be adjusted by agreement.',
            'Output is delivered as a structured PDF with the necessary annex documentation.',
          ].map((line) => (
            <p key={line} className="py-6 border-b border-memo-line text-sm" style={{ color: 'var(--memo-ink-2)' }}>{line}</p>
          ))}
        </div>
      </section>

      {/* ── Engagement Types — numbered ledger with marginal meta ── */}
      <section id="home-engagements" className="py-16 md:py-20 bg-memo-paper border-b border-memo-line">
        <div className="mb-10">
          <MemoEyebrow>Engagement Types</MemoEyebrow>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 mb-3">How We Work With You</h2>
        </div>

        <div className="flex flex-col gap-0">
          {ENGAGEMENTS.map((e) => (
            <div key={e.number} className="grid grid-cols-[3rem_1fr] lg:grid-cols-[4rem_1fr] gap-6 py-10 border-b border-memo-line">
              <SectionNum>{e.number}</SectionNum>
              <div>
                <h3 className="font-display text-2xl font-medium">{e.title}</h3>
                {e.subtitle && <p className="mt-1 font-mono text-xs uppercase tracking-[0.1em]" style={{ color: 'var(--memo-muted)' }}>{e.subtitle}</p>}
                {e.body && <p className="mt-4 text-[0.95rem] leading-relaxed max-w-[70ch]" style={{ color: 'var(--memo-ink-2)' }}>{e.body}</p>}
                {e.includes && (
                  <>
                    <p className="mt-4 text-xs text-memo-muted">Includes:</p>
                    <ul className="mt-1 space-y-2">
                      {e.includes.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <span className="text-memo-signal" aria-hidden="true">—</span>
                          <span className="text-[0.95rem]" style={{ color: 'var(--memo-ink-2)' }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {e.outputs && (
                  <>
                    <p className="mt-4 text-xs text-memo-muted">Outputs:</p>
                    <ul className="mt-1 space-y-2">
                      {e.outputs.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <span className="text-memo-signal" aria-hidden="true">—</span>
                          <span className="text-[0.95rem]" style={{ color: 'var(--memo-ink-2)' }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {e.meta && (
                  <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
                    {e.meta.map(([k, v]) => (
                      <div key={k} className="contents">
                        <dt className="font-mono text-xs uppercase tracking-[0.08em] text-memo-signal">{k}</dt>
                        <dd style={{ color: 'var(--memo-ink-2)' }}>{v}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Input Requirements — narrow editorial passage ── */}
      <section id="home-inputs" className="py-16 md:py-20 border-b border-memo-line">
        <div className="max-w-[66ch]">
          <MemoEyebrow>Input Requirements</MemoEyebrow>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 mb-3">Minimal, Available, Actionable</h2>
          <p className="text-lg mb-8" style={{ color: 'var(--memo-ink-2)' }}>Required exports typically include:</p>
          <div className="p-6 border border-memo-line bg-memo-paper rounded mb-6">
            <ul className="space-y-2">
              {INPUT_REQUIREMENTS.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="text-memo-signal mt-0.5" aria-hidden="true">✓</span>
                  <span style={{ color: 'var(--memo-ink-2)' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="text-sm" style={{ color: 'var(--memo-muted)' }}>
            No logins required. No system re-instrumentation. Your exports are enough.
          </p>
        </div>
      </section>

      {/* ── Who This Is For — fit matrix ── */}
      <section id="home-fit" className="py-16 md:py-20 bg-memo-paper border-b border-memo-line">
        <div className="mb-10">
          <MemoEyebrow>Who This Is For</MemoEyebrow>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3">And Who It Is Not For</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 overflow-hidden border border-memo-line-2 rounded">
          <div className="p-9 bg-memo-paper border-r border-memo-line">
            <p className="font-mono text-xs uppercase tracking-[0.14em] text-memo-signal mb-5">Strong fit if:</p>
            <ul className="space-y-3">
              {GOOD_FIT.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="text-memo-signal mt-0.5" aria-hidden="true">✓</span>
                  <span className="text-[0.95rem]" style={{ color: 'var(--memo-ink-2)' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-9 bg-memo-paper-2">
            <p className="font-mono text-xs uppercase tracking-[0.14em] text-memo-muted mb-5">Not a fit if:</p>
            <ul className="space-y-3">
              {NOT_FIT.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="text-memo-faint mt-0.5" aria-hidden="true">—</span>
                  <span className="text-[0.95rem]" style={{ color: 'var(--memo-muted)' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── Instability Atlas — teaser ── */}
      <section id="home-atlas" className="py-16 md:py-20 border-b border-memo-line">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          <div>
            <MemoEyebrow>Instability Atlas</MemoEyebrow>
            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 mb-4">Patterns, Archetypes, Truth</h2>
            <p className="text-lg mb-6" style={{ color: 'var(--memo-ink-2)' }}>
              The OnPoint Instability Atlas offers a systematic pattern library culled from actual breakdowns experienced in real operations.
            </p>
            <Link to="/atlas" data-testid="home-atlas-link" className="inline-flex items-center gap-2 text-[0.95rem] font-medium text-memo-signal hover:text-memo-accent transition-colors duration-150">
              Explore the Atlas
            </Link>
          </div>
          <div className="p-6 border border-memo-line-2 bg-memo-paper rounded">
            <h3 className="text-sm font-medium mb-4" style={{ color: 'var(--memo-ink)' }}>Patterns include:</h3>
            <ul className="space-y-3">
              {ATLAS_PATTERNS.map((pattern) => (
                <li key={pattern} className="flex items-start gap-3">
                  <span className="text-memo-signal mt-2 text-xs" aria-hidden="true">◦</span>
                  <span style={{ color: 'var(--memo-ink-2)' }}>{pattern}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── Final CTA — memorandum sign-off ── */}
      <section id="home-cta" className="py-16 md:py-20 bg-memo-paper-2 border-b border-memo-line border-t border-memo-line">
        <div className="max-w-[66ch]">
          <MemoEyebrow>Ready to proceed</MemoEyebrow>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 mb-4">
            If metrics are green but outcomes are unstable, that&rsquo;s structure.
          </h2>
          <p className="text-lg leading-relaxed mb-9" style={{ color: 'var(--memo-ink-2)' }}>
            Remove assumption from the equation.
          </p>
          <div className="flex flex-wrap gap-3">
            <CtaPrimary to="/intake" testid="footer-cta-intake">Begin Intake</CtaPrimary>
            <CtaGhost to="/demo" testid="footer-cta-demo">Diagnostics Preview</CtaGhost>
          </div>
        </div>
      </section>
        </div>
      </div>
    </div>
  );
}
